import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Clock, FileText, Paperclip, Truck, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoBuscaMultipla } from '@/componentes/CampoBuscaMultipla';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Janela, JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { PaginaSemPermissao, SemPermissao } from '@/componentes/SemPermissao';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { perfilAPedir } from '@/lib/perfil-da-licenca';
import { HistoriaNaMesa } from '@/historias/mesa';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarInteiro, interpretarNumero, formatarDia } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA, fatiarPagina } from '@/modulos/painel/painel';
import { useSessao } from '@/modulos/sessao/consultas';
import { rotuloDaEscolha } from '@/componentes/rotuloDaEscolha';
import { useCadastro, usePessoas } from '@/modulos/cadastros/consultas';
import { useCatalogo, useFrota } from '@/modulos/equipamentos/consultas-frota';

import { api, type Aptidao, type DocumentoNaLista, type Exigencia, type Impedimento } from './api';
import { contagemDosArquivos } from './arquivo';
import { ArquivosDoDocumento } from './ArquivosDoDocumento';
import { FuncoesDasPessoas } from './FuncoesDasPessoas';
import { CONSULTAS_DE_PESSOAS, abreAPaginaDePessoas, oQueLe } from './leitura';
import {
  alvoDaExigencia,
  aptidaoEscrita,
  documentoAtual,
  estadoDaFaixa,
  estadoEscrito,
  estaVigente,
  exigenciaPendenteDeValidacao,
  motivoEscrito,
  oQueFazer,
  ondeImpede,
  responsavelProposto,
  totaisDePessoas,
  erroDaValidade,
  validadeParaEnvio,
} from './pessoas';
import { opcaoDaMaquina, opcaoDaPessoa } from '@/modulos/equipamentos/opcoesComVisual';

/* As frases das listas de pessoas e de cadastros, iguais nas três janelas que as usam. */
const NENHUMA_PESSOA = 'Nenhuma pessoa em uso · cadastre em Cadastros › Pessoas';
const SEM_LISTA_DE_PESSOAS = 'O seu perfil não lê a lista de pessoas · peça a liberação em Cadastros › Governança';
const SEM_LISTA_DE_CADASTRO = 'O seu perfil não lê os cadastros · peça a liberação em Cadastros › Governança';

/**
 * MÓDULO 16 — pessoas, segurança e documentos. O quarto e último do ciclo 2.
 *
 * ## A tela abre no impedimento
 *
 * O v6 escreve o benefício do módulo assim: *"visão das pendências documentais
 * que podem IMPEDIR A OPERAÇÃO"*. Então a primeira coisa na tela não é a lista
 * de documentos — é o que está impedindo a operação **hoje**, por obra alocada,
 * com o nome de quem responde por aquele documento.
 *
 * A lista de documentos vem depois, e serve para outra pergunta: o que vence
 * nos próximos dias, para renovar antes. O risco que o v6 nomeia é "descobrir
 * o vencimento durante a mobilização".
 *
 * ## A tela não calcula validade
 *
 * O estado de cada documento vem do banco, pelo `aviso_dias` do tipo. Uma
 * segunda conta aqui divergiria na primeira vez que alguém trocasse o número de
 * dias — e aí a tela e o impedimento discordariam sobre a mesma CNH.
 *
 * ## E o impedimento não bloqueia
 *
 * Isso está escrito na migração e na resposta da rota. Transformar o
 * impedimento em recusa de alocação é decisão do dono: um sistema que barra
 * mobilização sozinho, no primeiro falso positivo — documento que existe em
 * papel e ninguém cadastrou —, ensina todo mundo a contornar o controle.
 */

export function PaginaPessoas() {
  const cliente = useQueryClient();
  const sessao = useSessao();
  const permissoes = useMemo(() => sessao.data?.permissoes ?? [], [sessao.data]);
  const podeGravar = permissoes.includes('documento_de_pessoa.gravar');
  /* Cada ação só para quem tem a licença dela (prova do marco, 07/10/2026):
     antes elas apareciam desligadas, com a licença no motivo. */
  const podeAutorizar = permissoes.includes('aptidao.gravar');
  /* CADA CONSULTA SÓ COM A LICENÇA DA ROTA DELA (achado dos robôs, 20/10/2026):
     sem isto, seis perfis abriam a tela com cinco 403 e um toast de permissão.
     Com a sessão em trânsito nada sai — `oQueLe(null)` não lê nada. */
  const le = useMemo(() => oQueLe(sessao.data?.permissoes ?? null), [sessao.data]);

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);

  /* AS QUATRO JANELAS DE CADASTRO. Uma única ação em laranja (o documento, que
     é a mais frequente); as outras três entram por "Mais ações" no cabeçalho,
     como o manual manda a partir de quatro ações (p.21). */
  type QualJanela = 'documento' | 'tipo' | 'exigencia' | 'aptidao';
  const [janela, setJanela] = useState<QualJanela | null>(null);
  /* O documento cujos arquivos estão abertos (0090): a cópia e a prova da regularização. */
  const [arquivosDe, setArquivosDe] = useState<DocumentoNaLista | null>(null);

  // Novo documento
  const [docTipoId, setDocTipoId] = useState('');
  const [docUsuarioId, setDocUsuarioId] = useState('');
  const [docEquipamentoId, setDocEquipamentoId] = useState('');
  const [docNumero, setDocNumero] = useState('');
  const [docValidoAte, setDocValidoAte] = useState('');
  const [docResponsavelId, setDocResponsavelId] = useState('');
  /* O QUE A PESSOA ESCREVEU no número e no responsável: a proposta (o documento
     que está sendo renovado, quem está cadastrando) não passa por cima. */
  const [docNumeroTocado, setDocNumeroTocado] = useState(false);
  const [docResponsavelTocado, setDocResponsavelTocado] = useState(false);

  // Novo tipo de documento
  const [tipoCodigo, setTipoCodigo] = useState('');
  const [tipoNome, setTipoNome] = useState('');
  const [tipoAplicaA, setTipoAplicaA] = useState('pessoa');
  const [tipoTemValidade, setTipoTemValidade] = useState('sim');
  const [tipoAvisoDias, setTipoAvisoDias] = useState('30');

  // Nova exigência
  const [exiTipoId, setExiTipoId] = useState('');
  const [exiContratoId, setExiContratoId] = useState('');
  const [exiObraId, setExiObraId] = useState('');
  const [exiObrigatorio, setExiObrigatorio] = useState('sim');

  // Nova autorização
  const [aptUsuarioId, setAptUsuarioId] = useState('');
  const [aptModelos, setAptModelos] = useState<readonly string[]>([]);
  const [aptConfiguracoes, setAptConfiguracoes] = useState<readonly string[]>([]);
  /* Pessoa, modelo e configuracao saem do que esta cadastrado: a janela pedia o
     identificador interno, que ninguem sabe de cabeca (achado no QAS em
     20/10/2026). Cada modelo ou configuracao marcado vira uma autorizacao. */
  /* Cada lista so e pedida com a janela que a usa aberta, e so por quem a le:
     sem isso, abrir a pagina sem governanca.ler dava aviso de falha. */
  const podeLerPessoas = permissoes.includes('governanca.ler');
  const podeLerCatalogo = permissoes.includes('catalogo.ler');
  const podeLerFrota = permissoes.includes('equipamento.ler');
  const podeLerCadastro = permissoes.includes('cadastro.ler');
  const pessoasParaAutorizar = usePessoas(podeLerPessoas && janela === 'documento');
  /* A JANELA DE AUTORIZAR LÊ A LISTA PELA LICENÇA DE AUTORIZAR (achado dos
     robôs, 20/10/2026): o Gestor operacional tem `aptidao.gravar` e não
     `governanca.ler`, e o campo Pessoa dizia que o perfil não lê a lista — a
     ação era oferecida e não se concluía. A rota `/pessoas/aptidoes/elegiveis`
     traz só o nome e o e-mail das pessoas ativas, com a licença de gravar. */
  const elegiveis = useQuery({
    queryKey: ['pessoas', 'elegiveis'],
    queryFn: api.elegiveis,
    enabled: le.elegiveis && janela === 'aptidao',
  });
  const opcoesDeElegivel = useMemo(
    () =>
      (elegiveis.data?.pessoas ?? [])
        .map((p) => opcaoDaPessoa(p, p.email))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [elegiveis.data],
  );
  const catalogoParaAutorizar = useCatalogo(podeLerCatalogo && janela === 'aptidao');
  const opcoesDePessoa = useMemo(
    () =>
      (pessoasParaAutorizar.data?.itens ?? [])
        .filter((p) => p.ativo)
        .map((p) => opcaoDaPessoa(p, p.email))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [pessoasParaAutorizar.data],
  );
  const opcoesDeModelo = useMemo(
    () =>
      (catalogoParaAutorizar.data?.modelos ?? [])
        .filter((m) => m.ativo)
        .map((m) => ({ valor: m.id, rotulo: m.fabricante ? `${m.fabricante} ${m.nome}` : m.nome }))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [catalogoParaAutorizar.data],
  );
  const opcoesDeConfiguracao = useMemo(
    () =>
      (catalogoParaAutorizar.data?.configuracoes ?? [])
        .filter((c) => c.ativo)
        .map((c) => ({ valor: c.id, rotulo: [c.fabricante, c.modelo, c.nome].filter(Boolean).join(' · ') }))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [catalogoParaAutorizar.data],
  );
  /* O mesmo achado nas janelas de documento e de exigência: máquina, contrato
     e obra pediam o identificador interno. Agora saem do que está em uso. */
  const frotaParaEscolher = useFrota(podeLerFrota && janela === 'documento');
  const contratosParaEscolher = useCadastro('contratos', podeLerCadastro && janela === 'exigencia');
  const obrasParaEscolher = useCadastro('obras', podeLerCadastro && janela === 'exigencia');
  const opcoesDeMaquina = useMemo(
    () =>
      (frotaParaEscolher.data ?? [])
        .filter((m) => m.ativo)
        .map((m) => opcaoDaMaquina(m))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [frotaParaEscolher.data],
  );
  const opcoesDeContrato = useMemo(
    () =>
      (contratosParaEscolher.data ?? [])
        .filter((c) => c.ativo)
        .map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.descricao ?? c.nome ?? ''}` }))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [contratosParaEscolher.data],
  );
  const opcoesDeObra = useMemo(
    () =>
      (obrasParaEscolher.data ?? [])
        .filter((o) => o.ativo)
        .map((o) => ({ valor: o.id, rotulo: `${o.codigo} · ${o.nome ?? o.descricao ?? ''}` }))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [obrasParaEscolher.data],
  );
  const [aptObservacao, setAptObservacao] = useState('');

  const impedimentos = useQuery({ queryKey: ['pessoas', 'impedimentos'], queryFn: api.impedimentos, enabled: le.impedimentos });
  const documentos = useQuery({ queryKey: ['pessoas', 'documentos'], queryFn: api.documentos, enabled: le.documentos });
  const aptidoes = useQuery({ queryKey: ['pessoas', 'aptidoes'], queryFn: api.aptidoes, enabled: le.aptidoes });
  const exigencias = useQuery({ queryKey: ['pessoas', 'exigencias'], queryFn: api.exigencias, enabled: le.exigencias });
  const tipos = useQuery({ queryKey: ['pessoas', 'tipos'], queryFn: api.tipos, enabled: le.tipos });

  const lista = useMemo(() => impedimentos.data?.impedimentos ?? [], [impedimentos.data]);
  const docs = useMemo(() => documentos.data?.documentos ?? [], [documentos.data]);
  /* As três listas inteiras, paginadas (p.26) — antes cortavam em 50 linhas
     sem dizer, e a 51ª não aparecia em lugar nenhum. */
  const paginaDosDocumentos = usePaginacao(docs);
  const paginaDasAutorizacoes = usePaginacao(aptidoes.data?.aptidoes ?? []);
  const paginaDasExigencias = usePaginacao(exigencias.data?.exigencias ?? []);
  const totais = useMemo(() => totaisDePessoas(lista, docs), [lista, docs]);
  const faixa = estadoDaFaixa(impedimentos);
  const fatia = fatiarPagina(lista, pagina, porPagina);
  const pendentesDeValidacao = useMemo(
    () => exigenciaPendenteDeValidacao(exigencias.data?.exigencias ?? []),
    [exigencias.data],
  );

  const recarregar = () => cliente.invalidateQueries({ queryKey: ['pessoas'] });
  const encerrarAptidao = useMutation({
    mutationFn: api.encerrarAptidao,
    meta: {
      retorno: retornoDe<unknown, string>((_r, id) => {
        const a = aptidoes.data?.aptidoes.find((x) => x.id === id);
        return {
          titulo: 'Autorização para operar encerrada',
          ...(a ? { alvo: { nome: a.pessoa } } : {}),
          fatos: [a ? (a.configuracao ?? a.modelo) : null, a ? `desde ${formatarDia(a.desde)}` : null],
          efeito: 'A vigência foi fechada hoje; a linha continua no histórico',
        };
      }),
    },
    onSuccess: recarregar,
  });
  const validar = useMutation({
    mutationFn: api.validarExigencia,
    meta: {
      retorno: retornoDe<unknown, string>((_r, id) => {
        const x = exigencias.data?.exigencias.find((e) => e.id === id);
        return {
          titulo: 'Exigência validada com o cliente',
          ...(x ? { alvo: { codigo: x.tipoCodigo, nome: x.tipo } } : {}),
          fatos: [x ? (x.contrato ?? x.obra) : null, x ? (x.obrigatorio ? 'obrigatória' : 'recomendada') : null],
        };
      }),
    },
    onSuccess: recarregar,
  });

  /* Cobrar a renovação (0073): o aviso vai só para quem responde pelo
     documento, uma vez por dia. É o mesmo botão do app (TelaDocumentos). */
  const cobrar = useMutation({
    mutationFn: (d: DocumentoNaLista) => api.cobrar(d.id),
    meta: {
      retorno: retornoDe<{ jaCobradoHoje: boolean }, DocumentoNaLista>((r, d) => ({
        titulo: r.jaCobradoHoje ? 'Renovação já cobrada hoje' : 'Renovação cobrada',
        tom: r.jaCobradoHoje ? 'ressalva' : 'sucesso',
        alvo: { codigo: d.tipoCodigo, nome: d.pessoa ?? d.equipamento ?? d.tipo },
        fatos: [d.responsavel ? `de ${d.responsavel}` : null, d.validoAte ? `vence em ${formatarDia(d.validoAte)}` : null],
        efeito: r.jaCobradoHoje
          ? 'A cobrança sai uma vez por dia; amanhã pode cobrar de novo'
          : 'O aviso aparece só para quem responde pelo documento',
      })),
    },
  });

  const fecharJanela = () => setJanela(null);

  /** A janela do documento, vazia ou já com o tipo e o dono de uma linha (Renovar documento). */
  function abrirDocumento(d?: DocumentoNaLista) {
    setDocTipoId(d?.tipoId ?? docTipoId);
    setDocUsuarioId(d?.usuarioId ?? '');
    setDocEquipamentoId(d?.equipamentoId ?? '');
    setDocNumero('');
    setDocValidoAte('');
    setDocNumeroTocado(false);
    setDocResponsavelTocado(false);
    setJanela('documento');
  }

  const gravarDocumento = useMutation({
    mutationFn: () =>
      api.gravarDocumento({
        tipoId: docTipoId,
        /* UM DONO, e exatamente um — o `check` do banco recusa os dois, e a
           guarda confere contra o `aplica_a` do tipo. A tela manda o que o
           tipo escolhido pede, e esconde o outro campo. */
        usuarioId: docUsuarioId.trim() === '' ? null : docUsuarioId,
        equipamentoId: docEquipamentoId.trim() === '' ? null : docEquipamentoId,
        numero: docNumero,
        /* Na tela, dd/mm/aaaa; no transporte, aaaa-mm-dd (achado dos robôs, 20/10/2026). */
        validoAte: validadeParaEnvio(docValidoAte),
        responsavelId: docResponsavelId.trim() === '' ? null : docResponsavelId,
      }),
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Documento registrado',
        ...(tipoEscolhido ? { alvo: { codigo: tipoEscolhido.codigo, nome: tipoEscolhido.nome } } : {}),
        fatos: [docNumero.trim() ? `número ${docNumero.trim()}` : null, validadeParaEnvio(docValidoAte) ? `válido até ${formatarDia(validadeParaEnvio(docValidoAte))}` : 'sem validade'],
      })),
    },
    onSuccess: async () => {
      fecharJanela();
      setDocNumero('');
      setDocValidoAte('');
      setDocUsuarioId('');
      setDocEquipamentoId('');
      await recarregar();
    },
  });

  const criarTipo = useMutation({
    mutationFn: () =>
      api.criarTipo({
        codigo: tipoCodigo,
        nome: tipoNome,
        aplicaA: tipoAplicaA === 'equipamento' ? 'equipamento' : 'pessoa',
        temValidade: tipoTemValidade === 'sim',
        avisoDias: interpretarNumero(tipoAvisoDias) ?? 30,
      }),
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Tipo de documento cadastrado',
        alvo: { codigo: tipoCodigo, nome: tipoNome },
        fatos: [
          tipoAplicaA === 'equipamento' ? 'de máquina' : 'de pessoa',
          tipoTemValidade === 'sim' ? `aviso ${formatarInteiro(interpretarNumero(tipoAvisoDias) ?? 30)} dias antes de vencer` : 'sem validade',
        ],
      })),
    },
    onSuccess: async () => {
      fecharJanela();
      setTipoCodigo('');
      setTipoNome('');
      await recarregar();
    },
  });

  const criarExigencia = useMutation({
    mutationFn: () =>
      api.criarExigencia({
        tipoId: exiTipoId,
        contratoId: exiContratoId.trim() === '' ? null : exiContratoId,
        obraId: exiObraId.trim() === '' ? null : exiObraId,
        obrigatorio: exiObrigatorio === 'sim',
      }),
    meta: {
      retorno: retornoDe(() => {
        const tipo = (tipos.data?.tipos ?? []).find((t) => t.id === exiTipoId);
        return {
          titulo: 'Exigência do cliente cadastrada',
          ...(tipo ? { alvo: { codigo: tipo.codigo, nome: tipo.nome } } : {}),
          fatos: [exiObrigatorio === 'sim' ? 'obrigatória' : 'recomendada'],
          efeito: 'Fica pendente até alguém validar com o cliente',
        };
      }),
    },
    onSuccess: async () => {
      fecharJanela();
      setExiContratoId('');
      setExiObraId('');
      await recarregar();
    },
  });

  const autorizar = useMutation({
    mutationFn: async () => {
      /* Uma autorizacao por modelo e por configuracao marcados, em fila: a
         recusa de uma (ja autorizada, por exemplo) para a fila e diz qual. */
      for (const modeloId of aptModelos) {
        await api.autorizar({ usuarioId: aptUsuarioId, modeloId, configuracaoId: null, observacao: aptObservacao });
      }
      for (const configuracaoId of aptConfiguracoes) {
        await api.autorizar({ usuarioId: aptUsuarioId, modeloId: null, configuracaoId, observacao: aptObservacao });
      }
    },
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Autorização para operar gravada',
        fatos: [
          opcoesDeElegivel.find((o) => o.valor === aptUsuarioId)?.rotulo ?? null,
          aptModelos.length > 0 ? `${String(aptModelos.length)} ${aptModelos.length === 1 ? 'modelo' : 'modelos'}` : null,
          aptConfiguracoes.length > 0 ? `${String(aptConfiguracoes.length)} ${aptConfiguracoes.length === 1 ? 'configuração' : 'configurações'}` : null,
          aptObservacao.trim() || null,
        ],
        efeito: 'Começa hoje e vale até alguém encerrar',
      })),
    },
    onSuccess: async () => {
      fecharJanela();
      setAptUsuarioId('');
      setAptModelos([]);
      setAptConfiguracoes([]);
      setAptObservacao('');
      await recarregar();
    },
  });

  /* As opções dos selects saem do que já está cadastrado. Lista vazia não vira
     select vazio: a janela diz o que falta cadastrar primeiro. */
  const opcoesDeTipo = useMemo(
    () => (tipos.data?.tipos ?? []).filter((t) => t.ativo).map((t) => ({
      valor: t.id,
      rotulo: `${t.codigo} · ${t.nome} (${t.aplicaA === 'pessoa' ? 'de pessoa' : 'de máquina'})`,
    })),
    [tipos.data],
  );
  const tipoEscolhido = useMemo(
    () => (tipos.data?.tipos ?? []).find((t) => t.id === docTipoId),
    [tipos.data, docTipoId],
  );

  /* RENOVAR JÁ VEM COM O QUE O DOCUMENTO TEM (31/10/2026): com o tipo e o dono
     escolhidos, o documento que existe dá o número e o responsável; sem ele,
     o responsável proposto é quem está cadastrando. */
  const documentoQueRenova = useMemo(
    () =>
      documentoAtual(documentos.data?.documentos ?? [], {
        tipoId: docTipoId,
        usuarioId: tipoEscolhido?.aplicaA === 'pessoa' ? docUsuarioId : '',
        equipamentoId: tipoEscolhido?.aplicaA === 'equipamento' ? docEquipamentoId : '',
      }),
    [documentos.data, docTipoId, docUsuarioId, docEquipamentoId, tipoEscolhido],
  );
  const responsavelDoDocumento = responsavelProposto(documentoQueRenova, sessao.data?.usuarioId ?? null, opcoesDePessoa);
  const numeroProposto = documentoQueRenova?.numero ?? '';
  const responsavelPropostoId = responsavelDoDocumento?.id ?? '';
  useEffect(() => {
    if (janela !== 'documento' || docNumeroTocado) return;
    setDocNumero(numeroProposto);
  }, [janela, docNumeroTocado, numeroProposto]);
  useEffect(() => {
    if (janela !== 'documento' || docResponsavelTocado) return;
    setDocResponsavelId(responsavelPropostoId);
  }, [janela, docResponsavelTocado, responsavelPropostoId]);

  const acoesDoCabecalho: readonly AcaoDoMenu[] = [
    ...(podeGravar
      ? [
          { rotulo: 'Cadastrar tipo de documento', aoEscolher: () => setJanela('tipo') },
          {
            rotulo: 'Cadastrar exigência do cliente',
            aoEscolher: () => setJanela('exigencia'),
            ...(opcoesDeTipo.length === 0
              ? { motivoIndisponivel: 'Cadastre ao menos um tipo de documento antes: a exigência aponta para um tipo.' }
              : {}),
          },
        ]
      : []),
    ...(podeAutorizar ? [{ rotulo: 'Autorizar alguém a operar', aoEscolher: () => setJanela('aptidao') }] : []),
  ];

  /* --------------------------------------------- a tabela de impedimentos */

  const COLUNAS: readonly Coluna<Impedimento>[] = [
    {
      chave: 'alvo',
      rotulo: 'Quem está impedido',
      celula: (i) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{ondeImpede(i)}</span>
          <TextoDaCelula
            texto={`${i.sobre === 'pessoa' ? 'Pessoa' : 'Máquina'} · ${i.tipo}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </span>
      ),
    },
    {
      chave: 'motivo',
      rotulo: 'Motivo',
      historia: 'COL-PES-01',
      /* Os três motivos escritos por extenso: eles impedem igual e se resolvem
         diferente, e "irregular" para os três manda procurar a coisa errada. */
      celula: (i) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{motivoEscrito(i.motivo)}</span>
          <TextoDaCelula texto={oQueFazer(i.motivo)} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'validade',
      rotulo: 'Validade',
      prioridade: 'secundaria',
      celula: (i) =>
        i.validoAte === null ? (
          <span className="text-tinta-secundaria">—</span>
        ) : (
          <span className="numero whitespace-nowrap text-parada-texto">
            {formatarDia(i.validoAte)}
          </span>
        ),
    },
    {
      chave: 'responsavel',
      rotulo: 'Responsável',
      prioridade: 'secundaria',
      /* Pendência sem dono é o risco da ferramenta 4. Quando não há
         responsável, a tela diz isso em vez de deixar a célula vazia. */
      celula: (i) =>
        i.responsavel === null ? (
          <span className="txt-apoio text-tinta-secundaria">Sem responsável definido</span>
        ) : (
          <span className="txt-corpo text-tinta">{i.responsavel}</span>
        ),
    },
    {
      chave: 'obrigatorio',
      rotulo: 'Impede',
      celula: (i) =>
        i.obrigatorio ? (
          <span className="txt-corpo text-tinta">Sim</span>
        ) : (
          <span className="txt-apoio text-tinta-secundaria">Recomendado</span>
        ),
    },
  ];

  /* ----------------------------------------------- a tabela de documentos */

  const COLUNAS_DE_DOCUMENTO: readonly Coluna<DocumentoNaLista>[] = [
    {
      chave: 'dono',
      rotulo: 'Documento',
      celula: (d) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{d.tipoCodigo}</span>
          <TextoDaCelula
            texto={`${d.pessoa ?? d.equipamento ?? 'Sem dono'} · ${d.tipo}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </span>
      ),
    },
    {
      chave: 'estado',
      rotulo: 'Estado',
      celula: (d) =>
        /* Colorido só quando o próprio valor é o alerta (regra 3). */
        d.estado === 'vencido' || d.estado === 'sem_data' ? (
          <span className="txt-corpo text-parada-texto">{estadoEscrito(d.estado)}</span>
        ) : (
          <span className="txt-corpo text-tinta">{estadoEscrito(d.estado)}</span>
        ),
    },
    {
      chave: 'validade',
      rotulo: 'Vence em',
      alinhamento: 'numero',
      celula: (d) =>
        d.validoAte === null ? (
          <span className="text-tinta-secundaria">—</span>
        ) : (
          <span className="numero whitespace-nowrap text-tinta">
            {formatarDia(d.validoAte)}
          </span>
        ),
    },
    {
      chave: 'dias',
      rotulo: 'Dias',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (d) =>
        d.diasParaVencer === null ? (
          <span className="text-tinta-secundaria">—</span>
        ) : (
          <span className="numero text-tinta">{formatarInteiro(d.diasParaVencer)}</span>
        ),
    },
    {
      /* O ARQUIVO (0090; desenho da R06, p.71): "Abrir" quando há arquivo,
         "Anexar" quando falta. Vale para o documento de pessoa e para o de
         máquina — os dois moram em `pess.documento` e usam o mesmo anexo.
         Abrir é por URL de cinco minutos, e só com a licença de ler documento. */
      chave: 'arquivo',
      rotulo: 'Arquivo',
      celula: (d) => {
        const quantos = (d.arquivos ?? 0) + (d.evidencias ?? 0);
        if (quantos === 0 && !podeGravar) return <span className="txt-apoio text-tinta-apagada">Sem arquivo</span>;
        return (
          <Botao
            nivel="fantasma"
            tamanho="p"
            title={contagemDosArquivos(d) ?? undefined}
            onClick={() => {
              setArquivosDe(d);
            }}
          >
            {quantos > 0 ? (
              <FileText aria-hidden="true" className="size-[var(--icone-controle)]" />
            ) : (
              <Paperclip aria-hidden="true" className="size-[var(--icone-controle)]" />
            )}
            {quantos > 0 ? 'Abrir' : 'Anexar'}
          </Botao>
        );
      },
    },
    {
      /* Só o vencido e o que vence logo se cobram, e só com responsável: o
         servidor recusa o documento sem ele (BTN-PES-05). */
      chave: 'cobrar',
      rotulo: 'Ação',
      alinhamento: 'numero',
      celula: (d) =>
        podeGravar ? (
          <span className="inline-flex flex-wrap justify-end gap-2">
            {d.responsavelId && (d.estado === 'vencido' || d.estado === 'vence_logo') ? (
              <HistoriaNaMesa codigo="BTN-PES-05" className="inline-flex">
                <Botao
                  nivel="terciario"
                  disabled={cobrar.isPending && cobrar.variables?.id === d.id}
                  onClick={() => {
                    cobrar.mutate(d);
                  }}
                >
                  Cobrar renovação
                </Botao>
              </HistoriaNaMesa>
            ) : null}
            {/* RENOVAR DA PRÓPRIA LINHA (31/10/2026): a janela abre com o tipo,
                o dono, o número e o responsável deste documento; falta só a
                validade nova. */}
            {d.estado !== 'valido' ? (
              <Botao
                nivel="terciario"
                onClick={() => {
                  abrirDocumento(d);
                }}
              >
                Renovar documento
              </Botao>
            ) : null}
          </span>
        ) : null,
    },
  ];

  const acoesDaAptidao = (a: Aptidao): readonly AcaoDoMenu[] => {
    const motivo = estaVigente(a) ? undefined : 'Esta autorização já foi encerrada.';
    const rotulo = 'Encerrar autorização';
    const aoEscolher = () => encerrarAptidao.mutate(a.id);
    return [motivo === undefined ? { rotulo, aoEscolher } : { rotulo, aoEscolher, motivoIndisponivel: motivo }];
  };

  const acoesDaExigencia = (x: Exigencia): readonly AcaoDoMenu[] => {
    const motivo =
      x.validadoEm !== null
        ? `Validada em ${formatarDia(x.validadoEm)}${x.validadoPor === null ? '' : ` por ${x.validadoPor}`}.`
        : undefined;
    const rotulo = 'Marcar como validada com o cliente';
    const aoEscolher = () => validar.mutate(x.id);
    return [motivo === undefined ? { rotulo, aoEscolher } : { rotulo, aoEscolher, motivoIndisponivel: motivo }];
  };

  /* O PERFIL QUE NÃO LÊ NADA DAQUI recebe a página sem permissão (p.34), e
     nenhuma consulta: nem impedimento, nem documento. A regra é a mesma que
     recorta o menu (`LEITURA_DA_TELA['/pessoas']`; ver `abreAPaginaDePessoas`). */
  const semLer = sessao.data != null && !abreAPaginaDePessoas(sessao.data.permissoes);
  if (semLer) {
    return (
      <PaginaSemPermissao
        sobretitulo="Pessoas, segurança e documentos"
        titulo="O que impede a operação hoje"
        modulo="as pessoas e os documentos"
        licenca={CONSULTAS_DE_PESSOAS.documentos.licenca}
      />
    );
  }
  /* Com a sessão chegada, o bloco que o perfil não lê vira o estado sem
     permissão desenhado, no lugar dele — nunca um toast. */
  const semImpedimentos = sessao.data !== undefined && !le.impedimentos;
  const semDocumentos = sessao.data !== undefined && !le.documentos;

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina flex flex-col gap-6">
        <CabecalhoPagina
          sobretitulo="Pessoas, segurança e documentos"
          titulo="O que impede a operação hoje"
          subtitulo="Por obra alocada, o documento exigido que está ausente, vencido ou sem validade — de máquina e de quem está autorizado a operá-la"
          acoes={
            <>
              {/* Secundários à esquerda, primário à direita (manual, p.21). */}
              {acoesDoCabecalho.length > 0 ? <MaisAcoes acoes={acoesDoCabecalho} /> : null}
              {podeGravar ? (
              <JanelaDeAcao
                rotulo="Cadastrar documento"
                titulo="Cadastrar ou renovar um documento"
                nivel="primario"
                aberta={janela === 'documento'}
                aoAbrir={() => {
                  abrirDocumento();
                }}
                aoFechar={fecharJanela}
                /* SEM `desligado` (achado dos robôs, 20/10/2026): sem tipo
                   cadastrado o botão ficava desligado e "não respondia ao
                   clique", sem dizer por quê. A janela abre e diz o que falta
                   — cadastrar o tipo primeiro, em Mais ações. */
                acoes={
                  <>
                    <Botao nivel="terciario" onClick={fecharJanela}>
                      Cancelar
                    </Botao>
                    <Botao
                      nivel="primario"
                      disabled={
                        docTipoId === ''
                        || (tipoEscolhido?.aplicaA === 'pessoa' ? docUsuarioId.trim() === '' : docEquipamentoId.trim() === '')
                        || erroDaValidade(docValidoAte, tipoEscolhido?.temValidade === true) !== null
                        || gravarDocumento.isPending
                      }
                      onClick={() => gravarDocumento.mutate()}
                    >
                      {gravarDocumento.isPending ? 'Gravando…' : 'Gravar documento'}
                    </Botao>
                  </>
                }
              >
                <div className="flex flex-col gap-4">
                  {tipos.isPending ? (
                    <ControleCarregando oQue="Carregando os tipos de documento" />
                  ) : tipos.isError ? (
                    <ErroDaLista oQue="Os tipos de documento" erro={tipos.error} aoTentarDeNovo={() => void tipos.refetch()} />
                  ) : opcoesDeTipo.length === 0 ? (
                    <p className="txt-apoio text-tinta-secundaria">
                      Nenhum tipo de documento cadastrado ainda. Cadastre os tipos que a operação usa
                      — CNH, ASO, seguro, laudo — em &ldquo;Mais ações&rdquo;, e depois volte aqui.
                    </p>
                  ) : (
                    <>
                      <p className="txt-apoio text-tinta-secundaria">
                        Renovar é gravar a data nova no mesmo documento: não existem dois documentos
                        válidos do mesmo tipo para a mesma pessoa ou máquina, porque dois fariam a
                        conferência escolher um e esconder qual.
                      </p>
                      <CampoSelecao
                        rotulo="Tipo de documento"
                        valor={docTipoId}
                        aoMudar={setDocTipoId}
                        opcoes={opcoesDeTipo}
                        rotuloVazio="Escolha o tipo"
                      />
                      {/* O CAMPO DO DONO SEGUE O TIPO: mostrar os dois convidaria
                          a preencher ambos, que o banco recusa. */}
                      {tipoEscolhido === undefined ? null : tipoEscolhido.aplicaA === 'pessoa' ? (
                        <CampoSelecao
                          rotulo="Pessoa"
                          valor={docUsuarioId}
                          aoMudar={setDocUsuarioId}
                          opcoes={opcoesDePessoa}
                          rotuloVazio={rotuloDaEscolha({
                            consulta: pessoasParaAutorizar,
                            pode: podeLerPessoas,
                            quantas: opcoesDePessoa.length,
                            escolha: 'Escolha a pessoa',
                            nenhuma: NENHUMA_PESSOA,
                            semPermissao: SEM_LISTA_DE_PESSOAS,
                          })}
                        />
                      ) : (
                        <CampoSelecao
                          rotulo="Máquina"
                          valor={docEquipamentoId}
                          aoMudar={setDocEquipamentoId}
                          opcoes={opcoesDeMaquina}
                          adesivo={{ porQueFora: 'a máquina está desativada no cadastro' }}
                          rotuloVazio={rotuloDaEscolha({
                            consulta: frotaParaEscolher,
                            pode: podeLerFrota,
                            quantas: opcoesDeMaquina.length,
                            escolha: 'Escolha a máquina',
                            nenhuma: 'Nenhuma máquina em uso · cadastre em Equipamentos',
                            semPermissao: 'O seu perfil não lê a frota · peça a liberação em Cadastros › Governança',
                          })}
                        />
                      )}
                      <CampoTexto
                        rotulo="Número"
                        valor={docNumero}
                        aoMudar={(valor) => {
                          setDocNumero(valor);
                          setDocNumeroTocado(true);
                        }}
                        opcional
                        {...(documentoQueRenova && !docNumeroTocado && docNumero !== '' ? { ajuda: 'O número do documento que está sendo renovado.' } : {})}
                      />
                      <HistoriaNaMesa codigo="CMP-PES-04" className="min-w-0">
                        <CampoTexto
                          rotulo="Válido até"
                          valor={docValidoAte}
                          aoMudar={setDocValidoAte}
                          placeholder="30/10/2026"
                          validar={(v) => erroDaValidade(v, tipoEscolhido?.temValidade === true)}
                          ajuda={
                            tipoEscolhido?.temValidade === false
                              ? 'Este tipo não vence, e a data é opcional.'
                              : documentoQueRenova?.validoAte
                                ? `Renovação: o atual vale até ${formatarDia(documentoQueRenova.validoAte)}. Escreva a validade nova, no formato dd/mm/aaaa.`
                                : 'No formato dd/mm/aaaa. Obrigatório: documento sem validade nunca aparece na lista de pendências.'
                          }
                        />
                      </HistoriaNaMesa>
                      <CampoSelecao
                        rotulo="Responsável por mantê-lo em dia"
                        valor={docResponsavelId}
                        aoMudar={(valor) => {
                          setDocResponsavelId(valor);
                          setDocResponsavelTocado(true);
                        }}
                        opcional
                        {...(!docResponsavelTocado && responsavelDoDocumento !== null && docResponsavelId === responsavelDoDocumento.id
                          ? { ajuda: responsavelDoDocumento.origem === 'documento' ? 'Quem já cuida deste documento.' : 'Você, que está cadastrando. Troque se outra pessoa cuida.' }
                          : {})}
                        opcoes={opcoesDePessoa}
                        rotuloVazio={rotuloDaEscolha({
                          consulta: pessoasParaAutorizar,
                          pode: podeLerPessoas,
                          quantas: opcoesDePessoa.length,
                          escolha: 'Sem responsável',
                          nenhuma: NENHUMA_PESSOA,
                          semPermissao: SEM_LISTA_DE_PESSOAS,
                        })}
                      />
                      <p className="txt-ajuda">
                        Sem responsável, a pendência não tem dono — e pendência sem dono é o risco que o documento v6 escreve.
                      </p>
                    </>
                  )}
                  {gravarDocumento.isError ? (
                    <p role="alert" className="txt-apoio text-parada-texto">
                      {explicarFalha(gravarDocumento.error).comoResolver}
                    </p>
                  ) : null}
                </div>
              </JanelaDeAcao>
              ) : null}
            </>
          }
        />

        {semImpedimentos ? (
          <SemPermissao
            titulo="Você não vê o que impede a operação"
            modulo="a lista do que impede a operação"
            perfil={perfilAPedir(CONSULTAS_DE_PESSOAS.impedimentos.licenca)}
          />
        ) : (
        <>
        {faixa === 'falhou' ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="flex flex-col gap-1">
              <span className="txt-corpo text-tinta">As pendências documentais não carregaram.</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(impedimentos.error).comoResolver}</span>
            </span>
            <Botao nivel="secundario" onClick={() => void impedimentos.refetch()}>
              Carregar de novo
            </Botao>
          </div>
        ) : (
          <>
            {/* Carregando (p.32): os quatro cards já se sabem — o rótulo e o
                ícone ficam, e só o número é esqueleto. */}
            <FaixaIndicadores>
              <CardIndicador
                rotulo="Máquinas impedidas"
                icone={Truck}
                tom="parada"
                carregando={faixa === 'carregando'}
                valor={formatarInteiro(totais.maquinasImpedidas)}
                alerta={totais.maquinasImpedidas > 0}
                contexto="Máquinas alocadas com documento obrigatório pendente"
              />
              <CardIndicador
                rotulo="Pessoas impedidas"
                icone={Users}
                tom="parada"
                carregando={faixa === 'carregando'}
                valor={formatarInteiro(totais.pessoasImpedidas)}
                alerta={totais.pessoasImpedidas > 0}
                contexto="Autorizadas a operar, com documento obrigatório pendente"
              />
              {semDocumentos ? null : (
              <>
              <CardIndicador
                rotulo="Documentos vencidos"
                icone={BookOpen}
                tom="standby"
                /* Os dois de documento saem da lista de documentos, que é
                   outra consulta: sem ela, diriam 0. */
                carregando={faixa === 'carregando' || documentos.isPending}
                valor={formatarInteiro(totais.vencidos)}
                alerta={totais.vencidos > 0}
                contexto="Em toda a base, não só nas máquinas alocadas"
              />
              <CardIndicador
                rotulo="Vencem logo"
                icone={Clock}
                tom="standby"
                carregando={faixa === 'carregando' || documentos.isPending}
                valor={formatarInteiro(totais.venceLogo)}
                contexto="Dentro do aviso em dias de cada tipo de documento"
              />
              </>
              )}
            </FaixaIndicadores>

            {/* A CONFERÊNCIA NÃO É BLOQUEIO, e isso é dito na tela. Sem a
                frase, uma faixa com zeros e uma com alertas se leem como
                "liberado" e "bloqueado" — e nenhuma das duas é verdade. */}
            <p className="txt-apoio text-pretty text-tinta-secundaria">
              Esta é uma conferência, e não um bloqueio: a alocação continua permitida com pendência.
              Transformar o impedimento em recusa é decisão do dono, e está registrada como pendente.
            </p>
          </>
        )}

        <Tabela
          rotulo="Impedimentos de hoje"
          colunas={COLUNAS}
          linhas={fatia.visiveis}
          chaveDaLinha={(i) => `${i.sobre}-${i.alvoId}-${i.tipoCodigo}-${i.obraId ?? i.contratoId ?? ''}`}
          carregando={impedimentos.isPending}
          alturaDupla
          erro={
            impedimentos.isError ? (
              <ErroDaLista oQue="Os impedimentos" erro={impedimentos.error} aoTentarDeNovo={() => void impedimentos.refetch()} />
            ) : null
          }
          vazio={
            <EstadoVazio
              titulo="Nenhum impedimento documental hoje"
              descricao="Todas as máquinas alocadas e quem está autorizado a operá-las têm os documentos que o contrato ou a obra exigem, dentro da validade."
            />
          }
          rodape={
            <RodapeTabela
              primeiro={fatia.primeiro}
              ultimo={fatia.ultimo}
              total={lista.length}
              pagina={fatia.pagina}
              paginas={fatia.paginas}
              porPagina={porPagina}
              opcoesPorPagina={OPCOES_POR_PAGINA}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={(novo) => {
                setPorPagina(novo);
                setPagina(1);
              }}
            />
          }
        />

        </>
        )}

        {semDocumentos ? (
          <>
            <h2 className="txt-secao text-tinta">Documentos, autorizações e exigências</h2>
            <SemPermissao
              titulo="Você não vê os documentos das pessoas e das máquinas"
              modulo="os documentos, as autorizações para operar, as funções e as exigências do cliente"
              perfil={perfilAPedir(CONSULTAS_DE_PESSOAS.documentos.licenca)}
            />
          </>
        ) : (
        <>
        <h2 className="txt-secao text-tinta">Documentos e vencimentos</h2>
        <Tabela
          rotulo="Documentos e vencimentos"
          colunas={COLUNAS_DE_DOCUMENTO}
          linhas={paginaDosDocumentos.visiveis}
          rodape={<RodapeTabela {...paginaDosDocumentos.rodape} />}
          chaveDaLinha={(d) => d.id}
          carregando={documentos.isPending}
          alturaDupla
          erro={
            documentos.isError ? (
              <ErroDaLista oQue="Os documentos" erro={documentos.error} aoTentarDeNovo={() => void documentos.refetch()} />
            ) : null
          }
          vazio={
            <EstadoVazio
              titulo="Nenhum documento cadastrado"
              descricao="Cadastre os tipos de documento que a operação usa — CNH, ASO, seguro, laudo — e depois os documentos de cada pessoa e máquina, com a validade."
            />
          }
        />

        <h2 className="txt-secao text-tinta">Quem está autorizado a operar</h2>
        <Tabela
          rotulo="Autorizações para operar"
          colunas={[
            {
              chave: 'pessoa',
              rotulo: 'Pessoa',
              celula: (a) => (
                <span className="flex min-w-0 flex-col">
                  <span className="txt-corpo text-tinta">{a.pessoa}</span>
                  <TextoDaCelula texto={aptidaoEscrita(a)} classe="txt-apoio text-tinta-apagada" linhas={1} />
                </span>
              ),
            },
            {
              chave: 'desde',
              rotulo: 'Desde',
              celula: (a) => (
                <span className="numero whitespace-nowrap text-tinta">
                  {formatarDia(a.desde)}
                </span>
              ),
            },
            {
              chave: 'vigente',
              rotulo: 'Situação',
              /* A encerrada CONTINUA NA LISTA: a história de quem estava
                 autorizado no dia do acidente é o que alguém vai perguntar. */
              celula: (a) =>
                estaVigente(a) ? (
                  <span className="txt-corpo text-tinta">Vigente</span>
                ) : (
                  <span className="txt-apoio text-tinta-secundaria">
                    {a.ate ? `Encerrada em ${formatarDia(a.ate)}` : 'Encerrada'}
                  </span>
                ),
            },
          ]}
          linhas={paginaDasAutorizacoes.visiveis}
          rodape={<RodapeTabela {...paginaDasAutorizacoes.rodape} />}
          chaveDaLinha={(a) => a.id}
          carregando={aptidoes.isPending}
          alturaDupla
          {...(podeAutorizar ? { acoes: (a: Aptidao) => <MaisAcoes acoes={acoesDaAptidao(a)} /> } : {})}
          erro={
            aptidoes.isError ? (
              <ErroDaLista oQue="As autorizações" erro={aptidoes.error} aoTentarDeNovo={() => void aptidoes.refetch()} />
            ) : null
          }
          vazio={
            <EstadoVazio
              titulo="Nenhuma autorização cadastrada"
              descricao="Autorize quem pode operar cada modelo ou configuração. A autorização é por tipo de máquina, e não por patrimônio: por máquina, ela envelheceria a cada equipamento novo."
            />
          }
        />

        {/* 0095 · as funções e quem exerce cada uma: a chave do valor-hora (Custos). */}
        <FuncoesDasPessoas podeGravar={podeAutorizar} podeLer={le.funcoes} />

        <h2 className="txt-secao text-tinta">Exigências do cliente</h2>
        {pendentesDeValidacao.length === 0 ? null : (
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            {pendentesDeValidacao.length === 1
              ? '1 exigência ainda não foi validada com o cliente. Até ser, ela impede a operação apoiada em quem a cadastrou.'
              : `${formatarInteiro(pendentesDeValidacao.length)} exigências ainda não foram validadas com o cliente. Até serem, elas impedem a operação apoiadas em quem as cadastrou.`}
          </p>
        )}
        <Tabela
          rotulo="Exigências do cliente"
          colunas={[
            {
              chave: 'tipo',
              rotulo: 'Documento exigido',
              celula: (x) => (
                <span className="flex min-w-0 flex-col">
                  <span className="codigo text-tinta">{x.tipoCodigo}</span>
                  <TextoDaCelula
                    texto={`${alvoDaExigencia(x)} · de ${x.aplicaA === 'pessoa' ? 'pessoa' : 'máquina'}`}
                    classe="txt-apoio text-tinta-apagada"
                    linhas={1}
                  />
                </span>
              ),
            },
            {
              chave: 'obrigatorio',
              rotulo: 'Impede',
              celula: (x) =>
                x.obrigatorio ? (
                  <span className="txt-corpo text-tinta">Sim</span>
                ) : (
                  <span className="txt-apoio text-tinta-secundaria">Recomendado</span>
                ),
            },
            {
              chave: 'validacao',
              rotulo: 'Validada',
              celula: (x) =>
                x.validadoEm === null ? (
                  <span className="txt-corpo text-parada-texto">Não validada</span>
                ) : (
                  <span className="txt-corpo text-tinta">
                    {formatarDia(x.validadoEm)}
                  </span>
                ),
            },
          ]}
          linhas={paginaDasExigencias.visiveis}
          rodape={<RodapeTabela {...paginaDasExigencias.rodape} />}
          chaveDaLinha={(x) => x.id}
          carregando={exigencias.isPending}
          alturaDupla
          {...(podeGravar ? { acoes: (x: Exigencia) => <MaisAcoes acoes={acoesDaExigencia(x)} /> } : {})}
          erro={
            exigencias.isError ? (
              <ErroDaLista oQue="As exigências" erro={exigencias.error} aoTentarDeNovo={() => void exigencias.refetch()} />
            ) : null
          }
          vazio={
            <EstadoVazio
              titulo="Nenhuma exigência cadastrada"
              descricao="Cadastre que documentos cada contrato ou obra exige. Sem a lista, não há o que conferir antes de uma mobilização."
            />
          }
        />
        </>
        )}
      </div>

      {/* ------------------------- o arquivo do documento e a regularização */}
      <Janela
        titulo="Arquivos do documento"
        {...(arquivosDe
          ? {
              descricao: `${arquivosDe.tipoCodigo} · ${arquivosDe.pessoa ?? arquivosDe.equipamento ?? arquivosDe.tipo}. A cópia do documento e a prova da regularização; nada se apaga.`,
            }
          : {})}
        aberta={arquivosDe !== null}
        aoFechar={() => {
          setArquivosDe(null);
        }}
      >
        {arquivosDe ? <ArquivosDoDocumento documento={arquivosDe} podeEnviar={podeGravar} /> : null}
      </Janela>

      {/* ---------------------------------- as três janelas de "Mais ações" */}

      <Janela
        titulo="Cadastrar tipo de documento"
        aberta={janela === 'tipo' && podeGravar}
        aoFechar={fecharJanela}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharJanela}>
              Cancelar
            </Botao>
            <Botao
              nivel="secundario"
              disabled={tipoCodigo.trim() === '' || tipoNome.trim() === '' || criarTipo.isPending}
              onClick={() => criarTipo.mutate()}
            >
              {criarTipo.isPending ? 'Cadastrando…' : 'Cadastrar tipo'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            O tipo é catálogo, e não texto livre: &ldquo;CNH&rdquo;, &ldquo;cnh&rdquo; e
            &ldquo;C.N.H.&rdquo; na mesma coluna fariam toda conferência por tipo falhar, e o
            conserto depois é adivinhação.
          </p>
          <CampoTexto rotulo="Código" valor={tipoCodigo} aoMudar={setTipoCodigo} ajuda="Curto e único. Por exemplo CNH, ASO, NR11, SEGURO." />
          <CampoTexto rotulo="Nome" valor={tipoNome} aoMudar={setTipoNome} />
          <CampoSelecao
            rotulo="Aplica-se a"
            valor={tipoAplicaA}
            aoMudar={setTipoAplicaA}
            opcoes={[
              { valor: 'pessoa', rotulo: 'Pessoa — CNH, ASO, treinamento' },
              { valor: 'equipamento', rotulo: 'Máquina — seguro, laudo, licença' },
            ]}
          />
          <CampoSelecao
            rotulo="Este documento vence"
            valor={tipoTemValidade}
            aoMudar={setTipoTemValidade}
            opcoes={[
              { valor: 'sim', rotulo: 'Sim, e a validade é obrigatória' },
              { valor: 'nao', rotulo: 'Não vence — certificado vitalício' },
            ]}
          />
          {tipoTemValidade === 'sim' ? (
            <CampoTexto
              rotulo="Avisar quantos dias antes"
              valor={tipoAvisoDias}
              aoMudar={setTipoAvisoDias}
              ajuda="É esse número que faz o documento aparecer como “vence logo”. Sem ele, antecipar a renovação é adivinhar."
            />
          ) : null}
          {criarTipo.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(criarTipo.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Cadastrar exigência do cliente"
        aberta={janela === 'exigencia' && podeGravar}
        aoFechar={fecharJanela}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharJanela}>
              Cancelar
            </Botao>
            <HistoriaNaMesa codigo="BTN-PES-03" className="flex">
              <Botao
                nivel="secundario"
                disabled={
                  exiTipoId === ''
                  || (exiContratoId.trim() === '') === (exiObraId.trim() === '')
                  || criarExigencia.isPending
                }
                onClick={() => criarExigencia.mutate()}
              >
                {criarExigencia.isPending ? 'Cadastrando…' : 'Cadastrar exigência'}
              </Botao>
            </HistoriaNaMesa>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            A exigência é do contrato ou de uma obra dele, nunca das duas: a da obra é mais
            específica, e a do contrato vale para todas as obras. Depois de cadastrada, marque-a como
            validada com o cliente — sem isso, &ldquo;o cliente exige&rdquo; é boato.
          </p>
          <CampoSelecao
            rotulo="Documento exigido"
            valor={exiTipoId}
            aoMudar={setExiTipoId}
            opcoes={opcoesDeTipo}
            rotuloVazio="Escolha o tipo"
          />
          <CampoSelecao
            rotulo="Contrato"
            valor={exiContratoId}
            aoMudar={setExiContratoId}
            opcional
            opcoes={opcoesDeContrato}
            rotuloVazio={rotuloDaEscolha({
              consulta: contratosParaEscolher,
              pode: podeLerCadastro,
              quantas: opcoesDeContrato.length,
              escolha: 'Nenhum (usar a obra)',
              nenhuma: 'Nenhum contrato em uso · cadastre em Cadastros › Contratos',
              semPermissao: SEM_LISTA_DE_CADASTRO,
            })}
          />
          <CampoSelecao
            rotulo="Obra"
            valor={exiObraId}
            aoMudar={setExiObraId}
            opcional
            opcoes={opcoesDeObra}
            rotuloVazio={rotuloDaEscolha({
              consulta: obrasParaEscolher,
              pode: podeLerCadastro,
              quantas: opcoesDeObra.length,
              escolha: 'Nenhuma (usar o contrato)',
              nenhuma: 'Nenhuma obra em uso · cadastre em Cadastros › Obras e locais',
              semPermissao: SEM_LISTA_DE_CADASTRO,
            })}
          />
          <CampoSelecao
            rotulo="A falta deste documento"
            valor={exiObrigatorio}
            aoMudar={setExiObrigatorio}
            opcoes={[
              { valor: 'sim', rotulo: 'Impede a operação' },
              { valor: 'nao', rotulo: 'É recomendada, e aparece na lista sem impedir' },
            ]}
          />
          {(exiContratoId.trim() === '') === (exiObraId.trim() === '') ? (
            <p className="txt-apoio text-tinta-secundaria">
              Escolha exatamente um dos dois: o contrato ou a obra.
            </p>
          ) : null}
          {criarExigencia.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(criarExigencia.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Autorizar alguém a operar"
        aberta={janela === 'aptidao' && podeAutorizar}
        aoFechar={fecharJanela}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharJanela}>
              Cancelar
            </Botao>
            <HistoriaNaMesa codigo="BTN-PES-02" className="flex">
              <Botao
                nivel="secundario"
                disabled={
                  aptUsuarioId.trim() === ''
                  || aptModelos.length + aptConfiguracoes.length === 0
                  || autorizar.isPending
                }
                onClick={() => autorizar.mutate()}
              >
                {autorizar.isPending ? 'Autorizando…' : 'Autorizar'}
              </Botao>
            </HistoriaNaMesa>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            A autorização é por modelo ou configuração, nunca por máquina: por patrimônio, ela
            obrigaria a refazer tudo a cada equipamento novo do mesmo tipo — e na prática ninguém
            refaz, então a autorização envelhece e deixa de valer. Ela começa hoje e não tem fim até
            alguém encerrá-la, e encerrar fecha a vigência em vez de apagar a linha.
          </p>
          <CampoSelecao
            rotulo="Pessoa"
            valor={aptUsuarioId}
            aoMudar={setAptUsuarioId}
            opcoes={opcoesDeElegivel}
            rotuloVazio={rotuloDaEscolha({
              consulta: elegiveis,
              pode: le.elegiveis,
              quantas: opcoesDeElegivel.length,
              escolha: 'Escolha a pessoa',
              nenhuma: NENHUMA_PESSOA,
              semPermissao: SEM_LISTA_DE_PESSOAS,
            })}
          />
          <CampoBuscaMultipla
            rotulo="Modelos"
            opcoes={opcoesDeModelo}
            marcados={aptModelos}
            aoMudar={setAptModelos}
            opcional
            ajuda="O modelo cobre todas as configurações dele. Marque quantos quiser."
            vazio="Nenhum modelo no catálogo. Cadastre em Equipamentos › Catálogo."
          />
          <CampoBuscaMultipla
            rotulo="Configurações"
            opcoes={opcoesDeConfiguracao}
            marcados={aptConfiguracoes}
            aoMudar={setAptConfiguracoes}
            opcional
            ajuda="Mais específica que o modelo: autoriza só aquela configuração."
            vazio="Nenhuma configuração no catálogo."
          />
          <CampoTexto rotulo="Observação" valor={aptObservacao} aoMudar={setAptObservacao} opcional />
          {aptModelos.length + aptConfiguracoes.length === 0 ? (
            <p className="txt-apoio text-tinta-secundaria">
              Marque ao menos um modelo ou uma configuração.
            </p>
          ) : null}
          {autorizar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(autorizar.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>
    </div>
  );
}
